(() => {
  "use strict";

  const MODAL_SELECTOR = "[data-peguu-samples-modal]";
  const OPEN_SELECTOR = "[data-peguu-samples-open]";
  const CLOSE_SELECTOR = "[data-peguu-samples-close]";
  const MATERIAL_URL = window.PeguuApi.endpoint("samplePack");
  const SAMPLE_PACK_CHECKOUT_URL = "/pages/arma-tus-stickers-resumen?open-payment=1";

  const formatPrice = (value, alwaysShowDecimals = false) => {
    if (value === null || value === undefined || value === "") return "";

    const price = Number(value);

    if (!Number.isFinite(price)) return "";

    const hasDecimals = !Number.isInteger(price);

    return `$${new Intl.NumberFormat("es-MX", {
      minimumFractionDigits: alwaysShowDecimals || hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(price)}`;
  };

  const parsePayload = async (response, fallbackMessage) => {
    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }
    if (!response.ok || !payload?.success) {
      const apiError = new Error(payload?.message || fallbackMessage);
      apiError.status = response.status;
      throw apiError;
    }
    return payload;
  };

  class PeguuSamplesModal {
    constructor(modal) {
      this.modal = modal;
      this.dialog = modal.querySelector("[data-peguu-samples-dialog]");
      this.closeButtons = modal.querySelectorAll(CLOSE_SELECTOR);
      this.buyButton = modal.querySelector("[data-peguu-samples-buy]");
      this.addButton = modal.querySelector("[data-peguu-samples-add]");
      this.priceValue = modal.querySelector("[data-peguu-samples-price-value]");
      this.buttonPrice = modal.querySelector("[data-peguu-samples-button-price]");
      this.buttonPriceValue = modal.querySelector("[data-peguu-samples-button-price-value]");
      this.message = modal.querySelector("[data-peguu-samples-message]");
      this.lastFocusedElement = null;
      this.isOpen = false;
      this.material = null;
      this.hasRequestedMaterial = false;
      this.messageTimer = null;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this.buy = this.buy.bind(this);
      this.addToCart = this.addToCart.bind(this);

      this.init();
    }

    init() {
      document.addEventListener("click", this.handleDocumentClick);
      document.addEventListener("keydown", this.handleKeydown);

      this.closeButtons.forEach((button) => {
        button.addEventListener("click", this.close);
      });
      this.buyButton?.addEventListener("click", this.buy);
      this.addButton?.addEventListener("click", this.addToCart);
    }

    async loadMaterial() {
      if (this.hasRequestedMaterial) return;
      this.hasRequestedMaterial = true;

      try {
        const response = await fetch(MATERIAL_URL, { headers: { Accept: "application/json" } });
        const payload = await parsePayload(response, "No fue posible consultar el paquete de muestras.");
        if (payload.data?.productCode !== "sample_pack") throw new Error("El paquete de muestras no está disponible.");
        const price = formatPrice(payload.data.purchase?.unitPrice);
        const buttonPrice = formatPrice(payload.data.purchase?.unitPrice, true);
        if (!price) throw new Error("El precio del paquete de muestras no está disponible.");
        this.material = payload.data;
        this.priceValue.textContent = price;
        this.buttonPriceValue.textContent = buttonPrice;
        this.buttonPrice.hidden = false;
        this.buyButton.setAttribute("aria-label", `Comprar paquete de muestras por ${buttonPrice}`);
        this.buyButton.disabled = false;
        this.addButton.disabled = false;
      } catch (error) {
        this.showMessage(
          error.message || "No fue posible consultar el paquete de muestras.",
          { temporary: true, duration: 5000 }
        );
      }
    }

    showMessage(message, options = {}) {
      if (!this.message) return;
      window.clearTimeout(this.messageTimer);
      this.message.textContent = message;
      this.message.hidden = false;

      if (options.temporary === true) {
        this.messageTimer = window.setTimeout(() => {
          this.clearMessage();
        }, options.duration || 4000);
      }
    }

    clearMessage() {
      if (!this.message) return;
      window.clearTimeout(this.messageTimer);
      this.messageTimer = null;
      this.message.textContent = "";
      this.message.hidden = true;
    }

    async buy() {
      if (!this.material || this.buyButton.disabled) return;
      this.clearMessage();
      this.buyButton.disabled = true;
      this.buyButton.setAttribute("aria-busy", "true");

      try {
        if (typeof window.PeguuCart?.addSamplePack !== "function") {
          throw new Error("El carrito no está disponible.");
        }

        await window.PeguuCart.addSamplePack(this.material);
        window.location.assign(SAMPLE_PACK_CHECKOUT_URL);
      } catch (error) {
        this.showMessage(error.message || "No fue posible iniciar la compra del paquete de muestras.");
        this.buyButton.disabled = false;
      } finally {
        this.buyButton.removeAttribute("aria-busy");
        if (!this.material) {
          this.buyButton.disabled = true;
        }
      }
    }

    async addToCart() {
      if (!this.material || this.addButton.disabled) return;
      this.clearMessage();
      this.addButton.disabled = true;
      this.addButton.setAttribute("aria-busy", "true");

      try {
        if (typeof window.PeguuCart?.addSamplePack !== "function") {
          throw new Error("El carrito no está disponible.");
        }

        await window.PeguuCart.addSamplePack(this.material);
        this.close();
        await window.PeguuCart.open();
      } catch (error) {
        this.showMessage(error.message || "No fue posible agregar el paquete de muestras al carrito.");
      } finally {
        this.addButton.removeAttribute("aria-busy");
        this.addButton.disabled = !this.material;
      }
    }

    handleDocumentClick(event) {
      const opener = event.target.closest(OPEN_SELECTOR);

      if (!opener) {
        return;
      }

      event.preventDefault();
      this.open(opener);
    }

    handleKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === "Escape") {
        this.close();
        return;
      }

      if (event.key === "Tab") {
        this.trapFocus(event);
      }
    }

    open(opener) {
      if (this.isOpen) {
        return;
      }

      this.loadMaterial();
      this.isOpen = true;
      this.lastFocusedElement = opener || document.activeElement;

      this.modal.classList.add("is-open");
      this.modal.setAttribute("aria-hidden", "false");

      document.documentElement.classList.add(
        "peguu-samples-modal-open"
      );

      document.body.classList.add(
        "peguu-samples-modal-open"
      );

      window.requestAnimationFrame(() => {
        this.dialog?.focus();
      });
    }

    close() {
      if (!this.isOpen) {
        return;
      }

      this.isOpen = false;
      this.resolveConfirmation?.(false);

      this.modal.classList.remove("is-open");
      this.modal.setAttribute("aria-hidden", "true");

      document.documentElement.classList.remove(
        "peguu-samples-modal-open"
      );

      document.body.classList.remove(
        "peguu-samples-modal-open"
      );

      if (
        this.lastFocusedElement &&
        typeof this.lastFocusedElement.focus === "function"
      ) {
        this.lastFocusedElement.focus();
      }
    }

    trapFocus(event) {
      const focusableElements = this.modal.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(",")
      );

      const elements = Array.from(focusableElements).filter(
        (element) => element.offsetParent !== null
      );

      if (!elements.length) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  const initialize = () => {
    const modal = document.querySelector(MODAL_SELECTOR);

    if (!modal || modal.dataset.peguuInitialized === "true") {
      return;
    }

    modal.dataset.peguuInitialized = "true";
    window.PeguuSamplesModal = new PeguuSamplesModal(modal);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", initialize);
})();
